import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  InputAdornment,
  makeStyles,
  Select,
  TextField,
  MenuItem,
  Typography,
  AccordionActions,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Box,
  DialogContentText,
  DialogContent,
  ListSubheader,
} from "@material-ui/core";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiboType, Finish } from "../types";
import { RootState } from "./store";
import fibo, { fiboSelectors } from "./store/fibo";
import ui from "./store/ui";
import DeleteIcon from "@material-ui/icons/Delete";
import CopyIcon from "@material-ui/icons/FileCopy";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { v4 as generateUuid } from "uuid";

import { ReactComponent as DualFibo } from "../svg/dual.svg";
import { ReactComponent as QuadFibo } from "../svg/quad.svg";

interface FinishEntry {
  id: number;
  text: string;
  hidden?: boolean;
}

const aluFinishes: FinishEntry[] = [
  {
    id: Finish.BrushedAluminium,
    text: "Brushed Aluminium",
  },
  // Note: Brushed dark grey was replaced by volcanic grey in april 2023
  {
    id: Finish.BrushedDarkGrey,
    text: "Brushed Dark Grey",
    hidden: true,
  },
  {
    id: Finish.BrushedVolcanicGrey,
    text: "Brushed Volcanic Grey",
  },
  {
    id: Finish.BrushedBlack,
    text: "Brushed Black",
  },
  {
    id: Finish.SatinWhite,
    text: "Satin White",
  },
];

const classicFinishes: FinishEntry[] = [
  {
    id: Finish.Bronze,
    text: "Bronze",
  },
  {
    id: Finish.BrushedNickel,
    text: "Brushed Nickel",
  },
  {
    id: Finish.BrushedBrass,
    text: "Brushed Brass",
  },
  {
    id: Finish.Rosé,
    text: "Rosé",
  },
];

const ferForgeFinishes: FinishEntry[] = [
  {
    id: Finish.FerForgéGrey,
    text: "Fer Forgé Grey",
  },
  {
    id: Finish.FerForgéBronze,
    text: "Fer Forgé Bronze",
  },
  {
    id: Finish.FerForgéGunmetal,
    text: "Fer Forgé Gunmetal",
  },
  {
    id: Finish.FerForgéRosé,
    text: "Fer Forgé Rosé",
  },
];

export const finishes = [
  ...aluFinishes,
  ...classicFinishes,
  ...ferForgeFinishes,
];

const useStyles = makeStyles((theme) => ({
  root: {
    border: "1px solid transparent",
  },
  expanded: {
    //backgroundColor: "#ccc",
    border: "1px solid #ccb25dcc",
  },
  spinner: {
    "& input": {
      appearance: "textfield",
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
      },
    },
  },
  toggleButtonGroup: {
    width: "100%",
    "& .MuiToggleButton-root": {
      color: "rgba(0, 0, 0, 0.80)",
      width: "100%",
      "&.Mui-selected": {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.default,
        boxShadow: `inset 0px 0px 0px 2px ${theme.palette.primary.light}`,
      },
    },
  },
  typeToggleSvg: {
    width: "50px",
    height: "60px",
    marginBottom: theme.spacing(1),
    fill: "currentColor",
    stroke: "none",
  },
  finishImage: {
    width: "64px",
    height: "48px",
    objectFit: "none",
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.grey[600],
    borderWidth: "1px",
    borderStyle: "solid",
  },
}));

const FinishImage = ({ finish }: { finish: Finish }) => {
  const classes = useStyles();

  return (
    <img
      className={classes.finishImage}
      src={`/assets/images/devices/0272/${finish
        .toString()
        .padStart(2, "0")}.png`}
      alt=""
    />
  );
};

const FinishMenuItem = ({ finish }: { finish: typeof finishes[0] }) => {
  return (
    <Box display="flex" alignItems="center">
      <FinishImage finish={finish.id} />
      <Box mx={2}>
        <Typography
          variant="button"
          style={{ textDecoration: finish.hidden ? "line-through" : "none" }}
        >
          {finish.text}
        </Typography>
      </Box>
    </Box>
  );
};

interface FiboDetailsProps {
  id: string;
}
function FiboDetails({ id }: FiboDetailsProps) {
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);

  const classes = useStyles();

  const dispatch = useDispatch();
  const activeId = useSelector((state: RootState) => state.uiState.activeId);
  const fibonacci = useSelector((state: RootState) =>
    fiboSelectors.selectById(state, id)
  );
  const ids = useSelector((state: RootState) => state.document.fibonaccis.ids);
  const currentIndex = ids.indexOf(fibonacci?.id ?? "");

  const expanded = activeId === id;

  if (!fibonacci) throw new Error("Fibonacci with this id not found!");

  const handleDelete = () => {
    dispatch(fibo.actions.fiboDeleted(fibonacci.id));

    const newId = ids[currentIndex + 1] ?? ids[currentIndex - 1];

    dispatch(ui.actions.setActiveId(newId as string));
    setConfirmationOpen(false);
  };

  const handleDuplicate = () => {
    const newId = generateUuid();
    dispatch(
      fibo.actions.fiboAdded({
        ...fibonacci,
        id: newId,
        description: fibonacci.description + " (copy)",
      })
    );
    dispatch(ui.actions.setActiveId(newId));
  };

  return (
    <>
      <Accordion
        className={`${classes.root} ${expanded ? classes.expanded : ""}`}
        expanded={expanded}
        onChange={() => dispatch(ui.actions.setActiveId(fibonacci.id))}
      >
        <AccordionSummary>
          <Box mr={1}>
            <Typography color="textSecondary">{fibonacci.amount}x</Typography>
          </Box>
          <Typography variant="button">{fibonacci.description}</Typography>
          <Box ml="auto" display="flex" alignItems="center">
            <Typography color="textSecondary" variant="caption">
              {fibonacci.type === FiboType.Dual ? "Dual" : "Quad"} -{" "}
              {finishes.find((f) => f.id === fibonacci.finish)?.text}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box width="100%">
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <TextField
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">x</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: "1" }}
                      fullWidth
                      defaultValue={fibonacci.amount}
                      onChange={(ev) =>
                        ev.target.value &&
                        dispatch(
                          fibo.actions.fiboUpdated({
                            id: fibonacci.id,
                            changes: {
                              amount: parseInt(ev.target.value),
                            },
                          })
                        )
                      }
                      className={classes.spinner}
                      variant="outlined"
                      size="small"
                      label="Amount"
                    />
                  </Grid>
                  <Grid item xs>
                    <TextField
                      fullWidth
                      value={fibonacci.description}
                      onChange={(ev) =>
                        dispatch(
                          fibo.actions.fiboUpdated({
                            id: fibonacci.id,
                            changes: { description: ev.target.value },
                          })
                        )
                      }
                      variant="outlined"
                      size="small"
                      label="Name"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box display="flex" justifyContent="center" mt={2} width="100%">
                <ToggleButtonGroup
                  className={classes.toggleButtonGroup}
                  value={fibonacci.type}
                  exclusive
                  onChange={(ev, value) =>
                    value !== null &&
                    dispatch(
                      fibo.actions.fiboUpdated({
                        id: fibonacci.id,
                        changes: { type: value as FiboType },
                      })
                    )
                  }
                >
                  <ToggleButton value={FiboType.Dual}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <DualFibo className={classes.typeToggleSvg} />
                      <Typography variant="button">Dual Fibonacci</Typography>
                    </Box>
                  </ToggleButton>
                  <ToggleButton value={FiboType.Quad}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <QuadFibo className={classes.typeToggleSvg} />
                      <Typography variant="button">Quad Fibonacci</Typography>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box display="flex" justifyContent="center" mt={2} width="100%">
                <Select
                  fullWidth
                  value={fibonacci.finish}
                  onChange={(ev) => {
                    if (ev.target.value)
                      dispatch(
                        fibo.actions.fiboUpdated({
                          id: fibonacci.id,
                          changes: { finish: ev.target.value as Finish },
                        })
                      );
                  }}
                  MenuProps={{
                    style: { maxHeight: "450px" },
                  }}
                  variant="outlined"
                  renderValue={(v) => {
                    const finishEntry = finishes.find((f) => f.id === v);
                    if (finishEntry) {
                      return <FinishMenuItem finish={finishEntry} />;
                    }

                    return <></>;
                  }}
                >
                  <ListSubheader disableSticky>Aluminium</ListSubheader>
                  {aluFinishes
                    .filter((f) => !f.hidden)
                    .map((finish) => (
                      <MenuItem value={finish.id} key={finish.id}>
                        <FinishMenuItem finish={finish} />
                      </MenuItem>
                    ))}
                  <ListSubheader disableSticky>Classic</ListSubheader>
                  {classicFinishes
                    .filter((f) => !f.hidden)
                    .map((finish) => (
                      <MenuItem value={finish.id} key={finish.id}>
                        <FinishMenuItem finish={finish} />
                      </MenuItem>
                    ))}
                  <ListSubheader disableSticky>Fer Forgé</ListSubheader>
                  {ferForgeFinishes
                    .filter((f) => !f.hidden)
                    .map((finish) => (
                      <MenuItem value={finish.id} key={finish.id}>
                        <FinishMenuItem finish={finish} />
                      </MenuItem>
                    ))}
                </Select>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
        <AccordionActions>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleDuplicate}
          >
            Duplicate
          </Button>
          {ids.length > 1 && (
            <Button
              color="secondary"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => {
                setConfirmationOpen(true);
              }}
            >
              Delete
            </Button>
          )}
          <Dialog
            open={confirmationOpen}
            onClose={() => {
              setConfirmationOpen(false);
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Delete Fibonacci?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this Fibonacci? This action
                cannot be undone!
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setConfirmationOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleDelete} color="secondary" autoFocus>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </AccordionActions>
      </Accordion>
    </>
  );
}

export default FiboDetails;
